import { maxAqiNext8h, fmtHour, aqiBand as aqiBandEn, aqiColor } from "../lib/aqi";
import type { AirQualityPoint } from "@sikhio/shared";

interface TrendPoint {
  at: string;
  aqi: number | null;
  pm25: number | null;
}

export interface AqiTrend {
  station: string;
  category: "good" | "moderate" | "unhealthy-sg" | "unhealthy" | "very-unhealthy" | "hazardous" | null;
  current: { aqi: number | null; pm25: number | null; observedAt: string };
  next8h: TrendPoint[];
  source: string;
}

interface Props {
  trend: AqiTrend | null;
  loading: boolean;
  /** Live station point (Air4Thai / Open-Meteo) when the 8h trend feed is empty. */
  livePoint?: AirQualityPoint | null;
}

const BAND: Record<
  NonNullable<AqiTrend["category"]>,
  { color: string; label: string; advice: string }
> = {
  good:            { color: "var(--aqi-good)",           label: "ดี",                    advice: "ทำกิจกรรมกลางแจ้งได้ตามปกติ" },
  moderate:        { color: "var(--aqi-moderate)",       label: "ปานกลาง",               advice: "กลุ่มเสี่ยง: ลดกิจกรรมกลางแจ้งที่ต้องออกแรงนาน" },
  "unhealthy-sg":  { color: "var(--aqi-unhealthy-sg)",   label: "กลุ่มเสี่ยงควรระวัง",   advice: "กลุ่มเสี่ยงควรลดกิจกรรมกลางแจ้ง" },
  unhealthy:       { color: "var(--aqi-unhealthy)",      label: "เริ่มมีผลต่อสุขภาพ",    advice: "ทุกคน: ลดกิจกรรมออกแรงกลางแจ้ง แนะนำสวมหน้ากาก" },
  "very-unhealthy":{ color: "var(--aqi-very-unhealthy)", label: "มีผลต่อสุขภาพมาก",      advice: "หลีกเลี่ยงออกแรงกลางแจ้ง สวมหน้ากาก N95 เมื่ออยู่นอกอาคาร" },
  hazardous:       { color: "var(--aqi-hazardous)",      label: "อันตราย",               advice: "อยู่ในอาคาร เปิดเครื่องฟอกอากาศ ยกเลิกกิจกรรมกลางแจ้ง" },
};

function categoryFromAqi(aqi: number): NonNullable<AqiTrend["category"]> {
  if (aqi <= 50) return "good";
  if (aqi <= 100) return "moderate";
  if (aqi <= 150) return "unhealthy-sg";
  if (aqi <= 200) return "unhealthy";
  if (aqi <= 300) return "very-unhealthy";
  return "hazardous";
}

export function AqiBadge({ trend, loading, livePoint = null }: Props) {
  const hasTrend = Boolean(trend && trend.current.aqi != null && trend.category != null);

  if (loading && !hasTrend && !livePoint) {
    return (
      <div className="aqi-badge" aria-busy>
        <div className="aqi-bar" style={{ background: "var(--line-2)" }} />
        <div className="aqi-readout">
          <div className="eyebrow mono">คุณภาพอากาศ · US AQI</div>
          <div className="aqi-num skeleton" style={{ width: 90, height: 56 }} />
          <div className="caption mono" style={{ color: "var(--text-3)" }}>กำลังเชื่อมต่อ…</div>
        </div>
      </div>
    );
  }

  // Prefer the 8h forecast card when present; otherwise show a live station readout.
  if (!hasTrend) {
    if (livePoint?.aqi == null) return null;
    const category = (livePoint.category as AqiTrend["category"]) ?? categoryFromAqi(livePoint.aqi);
    const band = category ? BAND[category] : {
      color: aqiColor(livePoint.aqi),
      label: aqiBandEn(livePoint.aqi),
      advice: "",
    };
    return (
      <section
        className="aqi-badge"
        role="status"
        aria-label={`Air quality ${band.label}, AQI ${livePoint.aqi}`}
        style={{ ["--aqi-color" as never]: band.color }}
      >
        <div className="aqi-bar" />
        <div className="aqi-readout">
          <div className="aqi-head">
            <span className="eyebrow mono">คุณภาพอากาศ · US AQI</span>
            <span className="aqi-band mono">{band.label}</span>
          </div>
          <div className="aqi-row">
            <div className="aqi-num" style={{ color: band.color }}>
              {Math.round(livePoint.aqi)}
            </div>
            <div className="aqi-stats mono">
              <div>
                <span className="caption">PM2.5</span>
                <strong>{(livePoint.pm25 ?? 0).toFixed(1)}</strong>
                <span className="caption" style={{ textTransform: "none" }}>µg/m³</span>
              </div>
            </div>
          </div>
          <div className="aqi-foot">
            <span className="caption">{livePoint.station ?? "Air4Thai"}</span>
            {livePoint.observedAt && (
              <span className="caption mono">{fmtHour(livePoint.observedAt)}</span>
            )}
          </div>
          {band.advice && <div className="aqi-advice">{band.advice}</div>}
        </div>
      </section>
    );
  }

  const t = trend!;
  const band = BAND[t.category!];
  const peak = maxAqiNext8h(t);
  const trending = peak - (t.current.aqi ?? 0);
  const trendArrow = trending >= 4 ? "▲" : trending <= -4 ? "▼" : "—";
  const trendDir = trending >= 4 ? "rising" : trending <= -4 ? "falling" : "stable";
  const trendColor = trending >= 4 ? "var(--bad)" : trending <= -4 ? "var(--good)" : "var(--text-2)";

  const series = [t.current.aqi, ...t.next8h.map((p) => p.aqi)].filter((v): v is number => v != null);
  const maxVal = Math.max(...series, 50);
  const minVal = Math.min(...series, 0);
  const span = Math.max(1, maxVal - minVal);
  const w = 200;
  const h = 32;
  const pts = series
    .map((v, i) => {
      const x = series.length > 1 ? (i / (series.length - 1)) * w : w / 2;
      const y = h - ((v - minVal) / span) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <section
      className="aqi-badge"
      role="status"
      aria-label={`Air quality ${band.label}, AQI ${t.current.aqi}`}
      style={{ ["--aqi-color" as never]: band.color }}
    >
      <div className="aqi-bar" />
      <div className="aqi-readout">
        <div className="aqi-head">
          <span className="eyebrow mono">คุณภาพอากาศ · US AQI</span>
          <span className="aqi-band mono">{band.label}</span>
        </div>
        <div className="aqi-row">
          <div className="aqi-num" style={{ color: band.color }}>
            {Math.round(t.current.aqi!)}
          </div>
          <div className="aqi-stats mono">
            <div>
              <span className="caption">PM2.5</span>
              <strong>{(t.current.pm25 ?? 0).toFixed(1)}</strong>
              <span className="caption" style={{ textTransform: "none" }}>µg/m³</span>
            </div>
            <div>
              <span className="caption">สูงสุด 8 ชม.</span>
              <strong style={{ color: trendColor }}>{Math.round(peak)}</strong>
              <span style={{ color: trendColor }} aria-label={trendDir}>{trendArrow}</span>
            </div>
          </div>
        </div>
        <svg viewBox={`0 0 ${w} ${h}`} className="aqi-spark" aria-hidden="true">
          <polyline points={pts} stroke={band.color} strokeWidth="1.5" fill="none" />
          <line x1="0" y1={h - ((100 - minVal) / span) * h} x2={w} y2={h - ((100 - minVal) / span) * h}
                stroke="var(--line-2)" strokeWidth="0.5" strokeDasharray="2 2" />
        </svg>
        <div className="aqi-foot">
          <span className="caption">{t.station}</span>
          <span className="caption mono">{fmtHour(t.current.observedAt)}</span>
        </div>
        <div className="aqi-advice">{band.advice}</div>
      </div>
    </section>
  );
}
